/**
 * @fileoverview Organization picker page displayed at the root URL (/).
 * Auto-redirects if the user has exactly 1 org; shows a selection grid
 * if multiple orgs; redirects to onboarding if no orgs.
 */

import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * OrgPicker page component.
 * - 0 orgs → redirect to /onboarding
 * - 1 org → auto-redirect to /org/:slug/dashboard
 * - Multiple orgs → show a card grid with links
 *
 * @returns {JSX.Element} The org picker page or a redirect
 */
function OrgPicker() {
  const { orgs, needsOnboarding, initialized, authenticated, isSuperAdmin, adminPath } = useAuth();

  if (!initialized) {
    return (
      <div className="org-picker-page">
        Loading...
      </div>
    );
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Super admin with no orgs — redirect to admin panel
  if (isSuperAdmin && adminPath && (!orgs || orgs.length === 0)) {
    return <Navigate to={adminPath} replace />;
  }

  if (!orgs || orgs.length === 0) {
    // Only redirect to onboarding if needsOnboarding is explicitly true.
    // When orgs is empty due to a failed API call (e.g., 500), avoid creating
    // a redirect loop between OrgPicker and Onboarding.
    if (needsOnboarding) {
      return <Navigate to="/onboarding" replace />;
    }
    return (
      <div className="org-picker-page">
        <div className="empty-state">
          <h2>Unable to load organizations</h2>
          <p>Please try refreshing the page.</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // Auto-redirect if user has exactly 1 org
  if (orgs.length === 1) {
    const slug = orgs[0].slug || orgs[0].id;
    return <Navigate to={`/org/${slug}/dashboard`} replace />;
  }

  // Multiple orgs — show picker
  return (
    <div className="org-picker-page">
      <h1 className="org-picker-title">Select Organization</h1>
      <p className="org-picker-subtitle">
        Choose which organization to work with
      </p>
      <div className="org-picker-grid">
        {orgs.map(org => (
          <Link
            key={org.id}
            to={`/org/${org.slug || org.id}/dashboard`}
            className="org-card"
          >
            <h3 className="org-card__name">{org.name}</h3>
            <span className="org-card__role">
              {org.role}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default OrgPicker;
